import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { isTransientNetworkIssue } from "../../utils/toastPolicy";
import useSettings from "../../hooks/useSettings";
import ClienteCustomFieldsForm from "../ClienteCustomFieldsForm";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: "0.75rem",
    marginBottom: theme.spacing(0.5),
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY = "clienteFieldRequirementsV1";

// Campos que SEMPRE aparecem no cadastro rápido (independente de configuração)
const ALWAYS_SHOWN_FIELDS = new Set([
  "NOMECLI", "PESSOA", "NOMEFANTASIA", "CGCCLI", "CPFCLI",
  "DDDCELULAR", "CELULAR", "DDDCLI", "FONECLI", "E_MAIL",
]);

const INITIAL_FORM = {
  NOMECLI: "",
  NOMEFANTASIA: "",
  PESSOA: "J",
  STATUS: "A",
  CGCCLI: "",
  CPFCLI: "",
  DDDCELULAR: "",
  CELULAR: "",
  DDDCLI: "",
  FONECLI: "",
  E_MAIL: "",
  INSCESTCLI: "",
  insc_municipal: "",
  suframa: "",
  contribuinte_icms: "Não",
  site: "",
  CEPCLI: "",
  ENDCLI: "",
  NUMERO: "",
  BAIRROCLI: "",
  CIDADECLI: "",
  ESTADOCLI: "",
  LIMITE: "",
  metas_vendas: "",
  potencial_vendas: "",
  acoes_vendas: "",
  OBS: "",
  USUARIO_RESPONSAVEL: "",
  customFields: {},
};

// Rótulos dos campos na mensagem de obrigatórios: os mesmos da tela de
// configuração de campos obrigatórios (settings.requiredFields)
const fieldLabel = (field) => {
  const key = `settings.requiredFields.fields.cliente.${field}`;
  return i18n.exists(key) ? i18n.t(key) : field;
};

// NOMECLI é varchar(60) no banco — acima disso o INSERT falha com "Data too long"
const NOMECLI_MAX_LENGTH = 60;

// Mesmo padrão de máscara do ClienteDadosGerais: estado guarda só dígitos, exibe formatado
const formatCNPJ = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const formatCPF = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

/**
 * Modal de cadastro rápido de cliente.
 * Carrega os fieldRequirements do sistema (clienteFieldRequirementsV1)
 * e exibe/valida dinamicamente os campos obrigatórios configurados.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): void
 *  - onSave(cliente): void  — chamado com o objeto do cliente criado
 *  - initialName: string   — preenche NOMECLI automaticamente (opcional)
 */
const ClienteQuickCreateModal = ({ open, onClose, onSave, initialName = "" }) => {
  const classes = useStyles();
  const { getAll: getAllSettings } = useSettings();
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [fieldRequirements, setFieldRequirements] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);

  // Carrega configurações de campos obrigatórios
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingSettings(true);
      try {
        const settings = await getAllSettings();
        if (!Array.isArray(settings)) return;
        const setting = settings.find((s) => s.key === CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY);
        if (setting?.value) {
          const parsed = JSON.parse(setting.value);
          setFieldRequirements(parsed?.cliente || {});
        }
      } catch (err) {
        console.error("Erro ao carregar configurações de campos:", err);
      } finally {
        setLoadingSettings(false);
      }
    };
    load();
  }, [open]);

  // Carrega usuários se o campo USUARIO_RESPONSAVEL for obrigatório
  useEffect(() => {
    if (!fieldRequirements.USUARIO_RESPONSAVEL) return;
    api.get("/users/list")
      .then(({ data }) => setUsuarios(data || []))
      .catch(() => {});
  }, [fieldRequirements.USUARIO_RESPONSAVEL]);

  // Reseta o form ao abrir
  useEffect(() => {
    if (open) {
      setFormData({
        ...INITIAL_FORM,
        NOMECLI: (initialName || "").slice(0, NOMECLI_MAX_LENGTH),
      });
    }
  }, [open, initialName]);

  const isRequired = (field) => Boolean(fieldRequirements[field]);
  // Mostra o campo se ele está sempre visível OU se é obrigatório na configuração
  const shouldShow = (field) => ALWAYS_SHOWN_FIELDS.has(field) || isRequired(field);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePhoneDigits = (field, maxDigits) => (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, maxDigits);
    setFormData((prev) => ({ ...prev, [field]: digits }));
  };

  const handleSubmit = async () => {
    const nome = (formData.NOMECLI || "").trim();
    if (!nome) {
      toast.warning(i18n.t("clienteQuickCreateModal.toasts.nameRequired"));
      return;
    }
    if (!formData.PESSOA) {
      toast.warning(i18n.t("clienteQuickCreateModal.toasts.personTypeRequired"));
      return;
    }

    // Valida campos obrigatórios da configuração do sistema
    const pessoa = String(formData.PESSOA || "").toUpperCase();
    const effectiveRequirements = { ...fieldRequirements };
    if (pessoa === "J") effectiveRequirements.CPFCLI = false;
    if (pessoa === "F") effectiveRequirements.CGCCLI = false;

    const missingFields = Object.keys(effectiveRequirements)
      .filter((f) => Boolean(effectiveRequirements[f]))
      .filter((f) => {
        const value = formData[f];
        return value === null || value === undefined || String(value).trim() === "";
      });

    if (missingFields.length > 0) {
      const labels = missingFields.map((f) => fieldLabel(f));
      toast.error(
        i18n.t("clienteQuickCreateModal.toasts.missingFields", {
          fields: labels.join(", "),
        })
      );
      return;
    }

    // Valida custom fields obrigatórios (espelha isEmptyRequiredValue do backend)
    const missingCustom = customFieldDefs
      .filter((d) => d.required)
      .filter((d) => {
        const v = (formData.customFields || {})[d.fieldName];
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim() === "";
        if (Array.isArray(v)) return v.length === 0;
        return false;
      });

    if (missingCustom.length > 0) {
      const labels = missingCustom.map((d) => d.fieldLabel || d.fieldName);
      toast.error(
        i18n.t("clienteQuickCreateModal.toasts.missingCustomFields", {
          fields: labels.join(", "),
        })
      );
      return;
    }

    setSubmitting(true);
    try {
      const raw = {
        ...formData,
        NOMECLI: nome.slice(0, NOMECLI_MAX_LENGTH),
        STATUS: formData.STATUS || "A",
      };

      // Mantém só o documento do tipo de pessoa selecionado
      if (formData.PESSOA === "J") {
        delete raw.CPFCLI;
      } else {
        delete raw.CGCCLI;
      }

      // Remove strings vazias (campos escondidos/não preenchidos): "" em
      // coluna numérica quebra o MySQL em modo estrito (ex.: LIMITE double)
      const payload = { customFields: formData.customFields || {} };
      Object.entries(raw).forEach(([key, value]) => {
        if (key === "customFields") return;
        if (typeof value === "string" && value.trim() === "") return;
        payload[key] = value;
      });

      if (payload.LIMITE !== undefined) {
        const limite = Number(payload.LIMITE);
        if (Number.isFinite(limite)) payload.LIMITE = limite;
        else delete payload.LIMITE;
      }
      if (payload.USUARIO_RESPONSAVEL !== undefined) {
        payload.USUARIO_RESPONSAVEL = Number(payload.USUARIO_RESPONSAVEL);
      }

      const { data } = await api.post("/clientes", payload);
      toast.success(i18n.t("clienteQuickCreateModal.toasts.created"));
      onSave(data);
    } catch (err) {
      // toastError suprime erros 502/503/504/rede; toast.warning não é
      // interceptado pela política — garante feedback neste fluxo
      if (isTransientNetworkIssue(err)) {
        toast.warning(i18n.t("clienteQuickCreateModal.toasts.networkUnstable"));
      } else {
        toastError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isPJ = formData.PESSOA === "J";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle>
        <Typography variant="h6">
          {i18n.t("clienteQuickCreateModal.title")}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("clienteQuickCreateModal.subtitle")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loadingSettings ? (
          <Grid container justifyContent="center" style={{ padding: 24 }}>
            <CircularProgress size={28} />
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {/* ── Tipo de pessoa ── */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{`${i18n.t("common.type")} *`}</InputLabel>
                <Select
                  value={formData.PESSOA}
                  onChange={handleChange("PESSOA")}
                  label={`${i18n.t("common.type")} *`}
                >
                  <MenuItem value="J">
                    {i18n.t("clienteQuickCreateModal.personTypes.company")}
                  </MenuItem>
                  <MenuItem value="F">
                    {i18n.t("clienteQuickCreateModal.personTypes.individual")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* ── Status ── */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small" required={isRequired("STATUS")}>
                <InputLabel>{i18n.t("common.status")}</InputLabel>
                <Select
                  value={formData.STATUS}
                  onChange={handleChange("STATUS")}
                  label={i18n.t("common.status")}
                >
                  <MenuItem value="A">{i18n.t("common.active")}</MenuItem>
                  <MenuItem value="I">{i18n.t("common.inactive")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* ── Nome / Razão Social ── */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                size="small"
                label={
                  isPJ
                    ? i18n.t("clienteQuickCreateModal.fields.legalName")
                    : i18n.t("common.name")
                }
                value={formData.NOMECLI}
                onChange={handleChange("NOMECLI")}
                variant="outlined"
                autoFocus
                inputProps={{ maxLength: NOMECLI_MAX_LENGTH }}
              />
            </Grid>

            {/* ── Nome fantasia ── */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={isRequired("NOMEFANTASIA")}
                size="small"
                label={
                  isPJ
                    ? i18n.t("settings.requiredFields.fields.cliente.NOMEFANTASIA")
                    : i18n.t("clienteQuickCreateModal.fields.nickname")
                }
                value={formData.NOMEFANTASIA}
                onChange={handleChange("NOMEFANTASIA")}
                variant="outlined"
              />
            </Grid>

            {/* ── CNPJ / CPF ── */}
            {shouldShow(isPJ ? "CGCCLI" : "CPFCLI") && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required={isRequired(isPJ ? "CGCCLI" : "CPFCLI")}
                  size="small"
                  label={isPJ ? "CNPJ" : "CPF"}
                  value={isPJ ? formatCNPJ(formData.CGCCLI) : formatCPF(formData.CPFCLI)}
                  onChange={
                    isPJ
                      ? handlePhoneDigits("CGCCLI", 14)
                      : handlePhoneDigits("CPFCLI", 11)
                  }
                  variant="outlined"
                  placeholder={isPJ ? "00.000.000/0000-00" : "000.000.000-00"}
                  inputProps={{ maxLength: isPJ ? 18 : 14 }}
                />
              </Grid>
            )}

            {/* ── Inscrição Estadual ── */}
            {shouldShow("INSCESTCLI") && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={isRequired("INSCESTCLI")}
                  size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.INSCESTCLI")}
                  value={formData.INSCESTCLI}
                  onChange={handleChange("INSCESTCLI")}
                  variant="outlined"
                />
              </Grid>
            )}

            {/* ── Inscrição Municipal ── */}
            {shouldShow("insc_municipal") && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={isRequired("insc_municipal")}
                  size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.insc_municipal")}
                  value={formData.insc_municipal}
                  onChange={handleChange("insc_municipal")}
                  variant="outlined"
                />
              </Grid>
            )}

            {/* ── SUFRAMA ── */}
            {shouldShow("suframa") && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={isRequired("suframa")}
                  size="small"
                  label="SUFRAMA"
                  value={formData.suframa}
                  onChange={handleChange("suframa")}
                  variant="outlined"
                />
              </Grid>
            )}

            {/* ── Contribuinte ICMS ── */}
            {shouldShow("contribuinte_icms") && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" size="small" required={isRequired("contribuinte_icms")}>
                  <InputLabel>{i18n.t("settings.requiredFields.fields.cliente.contribuinte_icms")}</InputLabel>
                  <Select
                    value={formData.contribuinte_icms}
                    onChange={handleChange("contribuinte_icms")}
                    label={i18n.t("settings.requiredFields.fields.cliente.contribuinte_icms")}
                  >
                    <MenuItem value="Sim">{i18n.t("common.yes")}</MenuItem>
                    <MenuItem value="Não">{i18n.t("common.no")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* ── Website ── */}
            {shouldShow("site") && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required={isRequired("site")}
                  size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.site")}
                  value={formData.site}
                  onChange={handleChange("site")}
                  variant="outlined"
                />
              </Grid>
            )}

            {/* ── Seção Contato ── */}
            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>
                {i18n.t("clienteQuickCreateModal.sections.contact")}
              </Typography>
            </Grid>

            {/* DDD + Celular */}
            <Grid item xs={3} sm={2}>
              <TextField
                fullWidth
                required={isRequired("DDDCELULAR")}
                size="small"
                label={i18n.t("clienteQuickCreateModal.fields.areaCode")}
                value={formData.DDDCELULAR}
                onChange={handlePhoneDigits("DDDCELULAR", 2)}
                variant="outlined"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={9} sm={4}>
              <TextField
                fullWidth
                required={isRequired("CELULAR")}
                size="small"
                label={i18n.t("settings.requiredFields.fields.cliente.CELULAR")}
                value={formData.CELULAR}
                onChange={handlePhoneDigits("CELULAR", 9)}
                variant="outlined"
              />
            </Grid>

            {/* DDD + Telefone */}
            <Grid item xs={3} sm={2}>
              <TextField
                fullWidth
                required={isRequired("DDDCLI")}
                size="small"
                label={i18n.t("clienteQuickCreateModal.fields.areaCode")}
                value={formData.DDDCLI}
                onChange={handlePhoneDigits("DDDCLI", 2)}
                variant="outlined"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={9} sm={4}>
              <TextField
                fullWidth
                required={isRequired("FONECLI")}
                size="small"
                label={i18n.t("common.phone")}
                value={formData.FONECLI}
                onChange={handlePhoneDigits("FONECLI", 9)}
                variant="outlined"
              />
            </Grid>

            {/* E-mail */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={isRequired("E_MAIL")}
                size="small"
                label={i18n.t("common.email")}
                type="email"
                value={formData.E_MAIL}
                onChange={handleChange("E_MAIL")}
                variant="outlined"
              />
            </Grid>

            {/* ── Seção Endereço (campos obrigatórios pela config) ── */}
            {(shouldShow("CEPCLI") || shouldShow("ENDCLI") || shouldShow("NUMERO") ||
              shouldShow("BAIRROCLI") || shouldShow("CIDADECLI") || shouldShow("ESTADOCLI")) && (
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  {i18n.t("clienteQuickCreateModal.sections.address")}
                </Typography>
              </Grid>
            )}

            {shouldShow("CEPCLI") && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required={isRequired("CEPCLI")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.CEPCLI")} value={formData.CEPCLI} onChange={handleChange("CEPCLI")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("ENDCLI") && (
              <Grid item xs={12} sm={8}>
                <TextField fullWidth required={isRequired("ENDCLI")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.ENDCLI")} value={formData.ENDCLI} onChange={handleChange("ENDCLI")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("NUMERO") && (
              <Grid item xs={12} sm={3}>
                <TextField fullWidth required={isRequired("NUMERO")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.NUMERO")} value={formData.NUMERO} onChange={handleChange("NUMERO")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("BAIRROCLI") && (
              <Grid item xs={12} sm={9}>
                <TextField fullWidth required={isRequired("BAIRROCLI")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.BAIRROCLI")} value={formData.BAIRROCLI} onChange={handleChange("BAIRROCLI")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("CIDADECLI") && (
              <Grid item xs={12} sm={8}>
                <TextField fullWidth required={isRequired("CIDADECLI")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.CIDADECLI")} value={formData.CIDADECLI} onChange={handleChange("CIDADECLI")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("ESTADOCLI") && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required={isRequired("ESTADOCLI")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.ESTADOCLI")} value={formData.ESTADOCLI} onChange={handleChange("ESTADOCLI")} variant="outlined"
                  inputProps={{ maxLength: 2 }} />
              </Grid>
            )}

            {/* ── Seção Comercial (campos obrigatórios pela config) ── */}
            {(shouldShow("LIMITE") || shouldShow("metas_vendas") || shouldShow("potencial_vendas") ||
              shouldShow("acoes_vendas") || shouldShow("USUARIO_RESPONSAVEL")) && (
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  {i18n.t("clienteQuickCreateModal.sections.commercial")}
                </Typography>
              </Grid>
            )}

            {shouldShow("USUARIO_RESPONSAVEL") && (
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small" required={isRequired("USUARIO_RESPONSAVEL")}>
                  <InputLabel>{i18n.t("settings.requiredFields.fields.cliente.USUARIO_RESPONSAVEL")}</InputLabel>
                  <Select
                    value={formData.USUARIO_RESPONSAVEL}
                    onChange={handleChange("USUARIO_RESPONSAVEL")}
                    label={i18n.t("settings.requiredFields.fields.cliente.USUARIO_RESPONSAVEL")}
                  >
                    <MenuItem value=""><em>{i18n.t("common.none")}</em></MenuItem>
                    {usuarios.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {shouldShow("LIMITE") && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required={isRequired("LIMITE")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.LIMITE")} type="number" value={formData.LIMITE}
                  onChange={handleChange("LIMITE")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("metas_vendas") && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required={isRequired("metas_vendas")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.metas_vendas")} value={formData.metas_vendas}
                  onChange={handleChange("metas_vendas")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("potencial_vendas") && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required={isRequired("potencial_vendas")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.potencial_vendas")} value={formData.potencial_vendas}
                  onChange={handleChange("potencial_vendas")} variant="outlined" />
              </Grid>
            )}
            {shouldShow("acoes_vendas") && (
              <Grid item xs={12}>
                <TextField fullWidth required={isRequired("acoes_vendas")} size="small"
                  label={i18n.t("settings.requiredFields.fields.cliente.acoes_vendas")} multiline rows={2} value={formData.acoes_vendas}
                  onChange={handleChange("acoes_vendas")} variant="outlined" />
              </Grid>
            )}

            {/* ── Observações ── */}
            {shouldShow("OBS") && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required={isRequired("OBS")}
                  size="small"
                  label={i18n.t("common.notes")}
                  multiline
                  rows={3}
                  value={formData.OBS}
                  onChange={handleChange("OBS")}
                  variant="outlined"
                />
              </Grid>
            )}

            {/* ── Campos Personalizados ── */}
            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>
                {i18n.t("clienteQuickCreateModal.sections.customFields")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ClienteCustomFieldsForm
                values={formData.customFields}
                onChange={(customFields) =>
                  setFormData((prev) => ({ ...prev, customFields }))
                }
                onDefinitionsLoaded={setCustomFieldDefs}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} color="default" disabled={submitting}>
          {i18n.t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || loadingSettings}
        >
          {submitting ? (
            <CircularProgress size={22} />
          ) : (
            i18n.t("clienteQuickCreateModal.buttons.create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClienteQuickCreateModal;

